import React, { useEffect, useState } from 'react';
import CollapseHeader from '../../../core/common/collapse-header/collapse-header';
import Table from '../../../core/common/dataTable/index';
import {
  createExternalContact,
  createExternalTemplate,
  deleteExternalContact,
  deleteExternalTemplate,
  fetchExternalContacts,
  fetchExternalParamFields,
  fetchExternalTemplates,
  fetchVendorWhatsappTemplates,
  updateExternalContact,
  updateExternalTemplate,
} from '../../../core/data/redux/service/OrgExternalUpdatesService';
import { formatError, formatSuccess } from '../../../core/data/redux/service/AuthService';
import { AxiosResponse } from 'axios';

const emptyContact = { name: '', mobileNumber: '', active: true };
const emptyTemplate = {
  displayName: '',
  vendorTemplate: '',
  type: 'TEMPLATE',
  language: 'en',
  customText: '',
  active: true,
  paramMapping: {
    param1: '',
    param2: '',
    param3: '',
    param4: '',
    param5: '',
    param6: '',
    param7: '',
    param8: '',
  } as Record<string, string>,
};

type VendorWhatsappTemplate = {
  id: string;
  name: string;
  template: string;
  type?: string;
  language?: string;
};

const OrgExternalUpdates = () => {
  const [tab, setTab] = useState<'contacts' | 'templates'>('contacts');
  const [contacts, setContacts] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [vendorTemplates, setVendorTemplates] = useState<VendorWhatsappTemplate[]>([]);
  const [paramFields, setParamFields] = useState<{ key: string; label: string }[]>([]);
  const [contactForm, setContactForm] = useState(emptyContact);
  const [editingContact, setEditingContact] = useState<any>(null);
  const [templateForm, setTemplateForm] = useState(emptyTemplate);
  const [editingTemplate, setEditingTemplate] = useState<any>(null);

  const loadContacts = () =>
    fetchExternalContacts()
      .then((res: AxiosResponse) => setContacts(res.data || []))
      .catch(err => formatError(err));

  const loadTemplates = () =>
    fetchExternalTemplates()
      .then((res: AxiosResponse) => setTemplates(res.data || []))
      .catch(err => formatError(err));

  useEffect(() => {
    loadContacts();
    loadTemplates();
    fetchExternalParamFields()
      .then((res: AxiosResponse) => setParamFields(res.data || []))
      .catch(err => formatError(err));
    fetchVendorWhatsappTemplates()
      .then((res: AxiosResponse) => setVendorTemplates(res.data || []))
      .catch(err => formatError(err));
  }, []);

  const deleteContact = (record: any) => {
    if (!record?.id) return;
    if (!window.confirm(`Deactivate external contact "${record.name}"? Their task subscriptions will also be deactivated.`)) {
      return;
    }
    deleteExternalContact(record.id)
      .then(() => {
        formatSuccess('Contact deactivated');
        if (editingContact?.id === record.id) {
          setEditingContact(null);
          setContactForm(emptyContact);
        }
        loadContacts();
      })
      .catch(err => formatError(err));
  };

  const contactColumns = [
    { title: 'Name', dataIndex: 'name' },
    { title: 'Mobile', dataIndex: 'mobileNumber' },
    { title: 'Active', dataIndex: 'active', render: (v: boolean) => (v ? 'Yes' : 'No') },
    {
      title: 'Action',
      render: (_: unknown, record: any) => (
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => {
              setEditingContact(record);
              setContactForm({
                name: record.name,
                mobileNumber: record.mobileNumber,
                active: record.active !== false,
              });
            }}
          >
            Edit
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={() => deleteContact(record)}
          >
            Deactivate
          </button>
        </div>
      ),
    },
  ];

  const deleteTemplate = (record: any) => {
    if (!record?.id) return;
    if (!window.confirm(`Deactivate template "${record.displayName}"?`)) return;
    deleteExternalTemplate(record.id)
      .then(() => {
        formatSuccess('Template deactivated');
        if (editingTemplate?.id === record.id) {
          setEditingTemplate(null);
          setTemplateForm(emptyTemplate);
        }
        loadTemplates();
      })
      .catch(err => formatError(err));
  };

  const onVendorTemplateChange = (vendorTemplateValue: string) => {
    const selected = vendorTemplates.find(t => t.template === vendorTemplateValue);
    setTemplateForm({
      ...templateForm,
      vendorTemplate: vendorTemplateValue,
      type: selected?.type || templateForm.type || 'TEMPLATE',
      language: selected?.language || templateForm.language || 'en',
      displayName: templateForm.displayName || selected?.name || '',
    });
  };

  const templateColumns = [
    { title: 'Name', dataIndex: 'displayName' },
    { title: 'Vendor template', dataIndex: 'vendorTemplate' },
    { title: 'Language', dataIndex: 'language' },
    { title: 'Active', dataIndex: 'active', render: (v: boolean) => (v ? 'Yes' : 'No') },
    {
      title: 'Action',
      render: (_: unknown, record: any) => (
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => {
              setEditingTemplate(record);
              setTemplateForm({
                displayName: record.displayName || '',
                vendorTemplate: record.vendorTemplate || '',
                type: record.type || 'TEMPLATE',
                language: record.language || 'en',
                customText: record.customText || '',
                active: record.active !== false,
                paramMapping: {
                  ...emptyTemplate.paramMapping,
                  ...(record.paramMapping || {}),
                },
              });
            }}
          >
            Edit
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={() => deleteTemplate(record)}
          >
            Deactivate
          </button>
        </div>
      ),
    },
  ];

  const submitContact = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = { ...contactForm, id: editingContact?.id };
    const req = editingContact
      ? updateExternalContact(payload as any)
      : createExternalContact(contactForm);
    req
      .then(() => {
        formatSuccess(editingContact ? 'Contact updated' : 'Contact created');
        setContactForm(emptyContact);
        setEditingContact(null);
        loadContacts();
      })
      .catch(err => formatError(err));
  };

  const submitTemplate = (e: React.FormEvent) => {
    e.preventDefault();
    const mapping: Record<string, string> = {};
    Object.entries(templateForm.paramMapping).forEach(([k, v]) => {
      if (v) mapping[k] = v;
    });
    const payload = {
      ...templateForm,
      id: editingTemplate?.id,
      paramMapping: mapping,
    };
    const req = editingTemplate
      ? updateExternalTemplate(payload)
      : createExternalTemplate(payload);
    req
      .then(() => {
        formatSuccess(editingTemplate ? 'Template updated' : 'Template created');
        setTemplateForm(emptyTemplate);
        setEditingTemplate(null);
        loadTemplates();
      })
      .catch(err => formatError(err));
  };

  const vendorTemplateInList = vendorTemplates.some(t => t.template === templateForm.vendorTemplate);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">External WhatsApp Updates</h2>
            <p className="text-muted mb-0">
              Manage external contacts and status-update WhatsApp templates. Subscribe contacts on individual tasks.
            </p>
          </div>
          <div className="head-icons ms-2">
            <CollapseHeader />
          </div>
        </div>

        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${tab === 'contacts' ? 'active' : ''}`}
              onClick={() => setTab('contacts')}
            >
              Contacts
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${tab === 'templates' ? 'active' : ''}`}
              onClick={() => setTab('templates')}
            >
              Status templates
            </button>
          </li>
        </ul>

        {tab === 'contacts' && (
          <>
            <div className="card mb-3">
              <div className="card-body">
                <form className="row g-3 align-items-end" onSubmit={submitContact}>
                  <div className="col-md-4">
                    <label className="form-label">Name</label>
                    <input
                      className="form-control"
                      value={contactForm.name}
                      onChange={e => setContactForm({ ...contactForm, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Mobile</label>
                    <input
                      className="form-control"
                      value={contactForm.mobileNumber}
                      onChange={e => setContactForm({ ...contactForm, mobileNumber: e.target.value })}
                      required
                    />
                  </div>
                  <div className="col-md-2 form-check mb-2 ms-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="contact-active"
                      checked={contactForm.active}
                      onChange={e => setContactForm({ ...contactForm, active: e.target.checked })}
                    />
                    <label className="form-check-label" htmlFor="contact-active">
                      Active
                    </label>
                  </div>
                  <div className="col-md-2">
                    <button type="submit" className="btn btn-primary w-100">
                      {editingContact ? 'Update' : 'Add contact'}
                    </button>
                  </div>
                  {editingContact && (
                    <div className="col-auto">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => {
                          setEditingContact(null);
                          setContactForm(emptyContact);
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </form>
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <Table dataSource={contacts} columns={contactColumns} Selection={false} rowKey="id" />
              </div>
            </div>
          </>
        )}

        {tab === 'templates' && (
          <>
            <div className="card mb-3">
              <div className="card-body">
                <form className="row g-3" onSubmit={submitTemplate}>
                  <div className="col-md-4">
                    <label className="form-label">Display name</label>
                    <input
                      className="form-control"
                      value={templateForm.displayName}
                      onChange={e => setTemplateForm({ ...templateForm, displayName: e.target.value })}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Vendor template (ORAI)</label>
                    <select
                      className="form-select"
                      value={templateForm.vendorTemplate}
                      onChange={e => onVendorTemplateChange(e.target.value)}
                      required
                    >
                      <option value="">Select ORAI template</option>
                      {templateForm.vendorTemplate && !vendorTemplateInList && (
                        <option value={templateForm.vendorTemplate}>
                          {templateForm.vendorTemplate} (saved)
                        </option>
                      )}
                      {vendorTemplates.map(t => (
                        <option key={t.id} value={t.template}>
                          {t.name} — {t.template}
                        </option>
                      ))}
                    </select>
                    {vendorTemplates.length === 0 && (
                      <small className="text-muted">
                        No WhatsApp templates found. Mark templates with whatsapp=true in the backend.
                      </small>
                    )}
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">Type</label>
                    <input
                      className="form-control"
                      value={templateForm.type}
                      onChange={e => setTemplateForm({ ...templateForm, type: e.target.value })}
                      required
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">Language</label>
                    <input
                      className="form-control"
                      value={templateForm.language}
                      onChange={e => setTemplateForm({ ...templateForm, language: e.target.value })}
                      required
                    />
                  </div>
                  <div className="col-md-8">
                    <label className="form-label">Custom text (for CUSTOM_TEXT param)</label>
                    <input
                      className="form-control"
                      value={templateForm.customText}
                      onChange={e => setTemplateForm({ ...templateForm, customText: e.target.value })}
                    />
                  </div>
                  <div className="col-md-2 form-check align-self-end mb-2 ms-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="template-active"
                      checked={templateForm.active}
                      onChange={e => setTemplateForm({ ...templateForm, active: e.target.checked })}
                    />
                    <label className="form-check-label" htmlFor="template-active">
                      Active
                    </label>
                  </div>
                  <div className="col-12">
                    <label className="form-label">Param mapping (Param1–8)</label>
                    <div className="row g-2">
                      {Object.keys(emptyTemplate.paramMapping).map(paramKey => (
                        <div className="col-md-3" key={paramKey}>
                          <select
                            className="form-select"
                            value={templateForm.paramMapping[paramKey] || ''}
                            onChange={e =>
                              setTemplateForm({
                                ...templateForm,
                                paramMapping: {
                                  ...templateForm.paramMapping,
                                  [paramKey]: e.target.value,
                                },
                              })
                            }
                          >
                            <option value="">{paramKey} — none</option>
                            {paramFields.map(f => (
                              <option key={f.key} value={f.key}>
                                {paramKey}: {f.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                    <small className="text-muted">
                      Only one active EXTERNAL_TASK_STATUS template is allowed per organisation.
                    </small>
                  </div>
                  <div className="col-md-3">
                    <button type="submit" className="btn btn-primary w-100">
                      {editingTemplate ? 'Update template' : 'Add template'}
                    </button>
                  </div>
                  {editingTemplate && (
                    <div className="col-md-2">
                      <button
                        type="button"
                        className="btn btn-outline-secondary w-100"
                        onClick={() => {
                          setEditingTemplate(null);
                          setTemplateForm(emptyTemplate);
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </form>
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <Table dataSource={templates} columns={templateColumns} Selection={false} rowKey="id" />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrgExternalUpdates;
